import { Controller } from "@hotwired/stimulus"

// Config-driven filter controller for Claims Dashboard.
// Reads filter registry from data attributes, manages draft/apply pattern,
// renders chips, dropdown panels, and serializes filters to URL params for Turbo Frame navigation.
export default class extends Controller {
  static targets = [
    "toggleButton", "buttonLabel", "noFiltersText",
    "chipsContainer", "overflowButton", "clearAllButton",
    "dropdown", "categoryList", "rightPanel", "draftCount"
  ]

  static values = {
    config: Array,
    activeFilters: Object,
    autocompleteUrl: String,
    turboFrame: String
  }

  connect() {
    this.MAX_VISIBLE_CHIPS = 3
    this.draftFilters = {}
    this.selectedCategory = null
    this.isOpen = false
    this.searchTimeout = null
    this.abortController = null

    // Click outside handler
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("mousedown", this.handleClickOutside)

    // Keyboard handler
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)

    this.renderChips()
    this.updateButtonLabel()
  }

  disconnect() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    if (this.abortController) this.abortController.abort()
  }

  // --- Dropdown open/close ---

  toggleDropdown(event) {
    event?.stopPropagation()
    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown(initialCategory = null) {
    this.draftFilters = JSON.parse(JSON.stringify(this.activeFiltersValue))
    this.selectedCategory = initialCategory || this.configValue[0]?.key || null
    this.isOpen = true
    this.dropdownTarget.classList.remove("hidden")
    // Primary button style is handled by global CSS; no class changes needed on open
    this.renderCategoryList()
    this.renderRightPanel()
    this.updateDraftCount()
  }

  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
    // Primary button style is handled by global CSS; no class changes needed on close
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    if (this.abortController) this.abortController.abort()
  }

  cancel() {
    this.closeDropdown()
  }

  handleClickOutside(event) {
    if (!this.isOpen) return
    if (this.element.contains(event.target)) return
    this.closeDropdown()
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    if (event.key === "Escape") {
      this.closeDropdown()
    }
  }

  onDropdownClick(event) {
    // Prevent clicks inside dropdown from bubbling to close handler
    event.stopPropagation()
  }

  // --- Apply / Clear ---

  apply() {
    const invalidRange = this.configValue.find(config => {
      const value = this.draftFilters[config.key]?.[0]
      if (config.type !== "date_range" || !config.require_complete || !value || this.isRelativeDateRange(value)) return false
      const [from, to] = value.split("|")
      return !from || !to || from > to
    })
    if (invalidRange) {
      this.selectedCategory = invalidRange.key
      this.renderCategoryList()
      this.renderRightPanel()
      Array.from(this.rightPanelTarget.querySelectorAll('input[type="date"]')).find(input => !input.checkValidity())?.reportValidity()
      return
    }
    this.activeFiltersValue = JSON.parse(JSON.stringify(this.draftFilters))
    this.closeDropdown()
    this.renderChips()
    this.updateButtonLabel()
    this.navigateWithFilters()
    this.dispatch("changed", { detail: { filters: this.activeFiltersValue } })
  }

  clearAll() {
    this.activeFiltersValue = {}
    this.renderChips()
    this.updateButtonLabel()
    this.navigateWithFilters()
    this.dispatch("changed", { detail: { filters: this.activeFiltersValue } })
  }

  removeChip(key, value) {
    const filters = JSON.parse(JSON.stringify(this.activeFiltersValue))
    if (filters[key]) {
      filters[key] = filters[key].filter(v => v !== value)
      if (filters[key].length === 0) delete filters[key]
    }
    this.activeFiltersValue = filters
    this.renderChips()
    this.updateButtonLabel()
    this.navigateWithFilters()
    this.dispatch("changed", { detail: { filters: this.activeFiltersValue } })
  }

  // --- URL serialization & Turbo navigation ---

  navigateWithFilters() {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams

    // Clear all filter-related params
    const registryKeys = this.configValue.map(f => f.key)
    for (const key of registryKeys) {
      searchParams.delete(key)
      searchParams.delete(`${key}_from`)
      searchParams.delete(`${key}_to`)
      searchParams.delete(`${key}_relative`)
      searchParams.delete(`${key}_exact`)
      searchParams.delete(`${key}_min`)
      searchParams.delete(`${key}_max`)
    }

    // Remove malformed array/hash variants too, so server validation errors
    // can be recovered through Clear all or by removing the affected chip.
    for (const key of [...searchParams.keys()]) {
      if (registryKeys.some(filterKey => key.startsWith(`${filterKey}[`))) searchParams.delete(key)
    }

    // Set active filter params
    const filters = this.activeFiltersValue
    for (const config of this.configValue) {
      if (config.type === "date_range" && config.comparison) {
        searchParams.set(config.comparison.key, this.comparisonEnabled(config, filters) ? "1" : "0")
      }
    }
    for (const [key, values] of Object.entries(filters)) {
      if (!values || values.length === 0) continue
      const config = this.configValue.find(f => f.key === key)
      if (!config) continue

      if (config.type === "text_input") {
        if (values[0]) searchParams.set(key, values[0])
      } else if (config.type === "date_input") {
        if (values[0]) searchParams.set(key, values[0])
      } else if (config.type === "date_range") {
        const dateStr = values[0] || ""
        if (this.isRelativeDateRange(dateStr)) {
          const preset = this.relativePresetKey(dateStr)
          const [from, to] = this.resolveRelativeDateRange(preset)
          searchParams.set(`${key}_relative`, preset)
          if (from) searchParams.set(`${key}_from`, from)
          if (to) searchParams.set(`${key}_to`, to)
        } else {
          const [from, to] = dateStr.split("|")
          if (from) searchParams.set(`${key}_from`, from)
          if (to) searchParams.set(`${key}_to`, to)
        }
      } else if (config.type === "currency_range" || config.type === "integer_range") {
        const amtStr = values[0] || ""
        const [exact, min, max] = amtStr.split("|")
        if (exact) searchParams.set(`${key}_exact`, exact)
        if (min) searchParams.set(`${key}_min`, min)
        if (max) searchParams.set(`${key}_max`, max)
      } else if (config.type === "enum_select") {
        searchParams.set(key, values.join(","))
      } else if (config.type === "boolean_select") {
        searchParams.set(key, values[0] || "")
      } else {
        // text_search: comma-separated
        searchParams.set(key, values.join(","))
      }
    }

    // Reset pagination
    searchParams.delete("page")
    searchParams.delete("export_page")

    const frameId = this.turboFrameValue
    if (frameId && document.getElementById(frameId)) {
      // Promote the frame navigation to a Turbo Visit so the URL + history update and the
      // filtered view is restorable on Back. Assigning frame.src directly does NOT consult
      // data-turbo-action in Turbo 8, so we use the public Turbo.visit frame API.
      Turbo.visit(url.toString(), { frame: frameId, action: "replace" })
      return
    }
    // Fallback: full page navigation
    window.location.href = url.toString()
  }

  // --- Rendering ---

  updateButtonLabel() {
    const count = this.getAllChips().length
    if (count > 0) {
      this.buttonLabelTarget.textContent = `Filters (${count})`
    } else {
      this.buttonLabelTarget.textContent = "Add filters"
    }
  }

  getAllChips() {
    const chips = []
    const filters = this.activeFiltersValue
    for (const [key, values] of Object.entries(filters)) {
      const config = this.configValue.find(f => f.key === key)
      if (!config) continue
      for (const val of values) {
        let label
        if (config.type === "enum_select" || config.type === "boolean_select") {
          const opt = this.findEnumOption(config, val)
          label = opt ? opt.label : val
          if (config.type === "boolean_select") {
            label = `${config.label}: ${label}`
          }
        } else if (config.type === "text_input") {
          label = `${config.label}: ${val}`
        } else if (config.type === "date_input") {
          label = `${config.label}: ${val}`
        } else if (config.type === "date_range") {
          label = `${config.label}: ${this.formatDateRange(val)}`
          if (config.comparison && this.comparisonEnabled(config, filters)) label += " · vs. last period"
        } else if (config.type === "currency_range") {
          label = `${config.label}: ${this.formatCurrencyRange(val)}`
        } else if (config.type === "integer_range") {
          label = `${config.label}: ${this.formatIntegerRange(val)}`
        } else {
          label = this.chipLabelForValue(val)
        }
        chips.push({ key, value: val, label })
      }
    }
    return chips
  }

  renderChips() {
    const allChips = this.getAllChips()
    const visibleChips = allChips.slice(0, this.MAX_VISIBLE_CHIPS)
    const overflowCount = allChips.length - this.MAX_VISIBLE_CHIPS
    const hasFilters = allChips.length > 0

    // Clear chips container
    this.chipsContainerTarget.innerHTML = ""

    // Render visible chips
    for (const chip of visibleChips) {
      const el = this.createChipElement(chip)
      this.chipsContainerTarget.appendChild(el)
    }

    // Show/hide no filters text
    this.noFiltersTextTarget.classList.toggle("hidden", hasFilters)

    // Show/hide overflow button
    if (overflowCount > 0) {
      this.overflowButtonTarget.textContent = `+${overflowCount} more`
      this.overflowButtonTarget.classList.remove("hidden")
    } else {
      this.overflowButtonTarget.classList.add("hidden")
    }

    // Show/hide clear all
    this.clearAllButtonTarget.classList.toggle("hidden", !hasFilters)
  }

  createChipElement(chip) {
    // Mirrors shared/_chip.html.erb partial: px-4 py-2 rounded-lg border, dismissible X
    const el = document.createElement("div")
    el.className = "px-4 py-2 rounded-lg justify-end items-center gap-4 inline-flex border bg-support-light-250 text-support-dark-400 border-support-light-350 hover:bg-support-light-300 hover:text-support-dark-500 hover:border-support-light-400 whitespace-nowrap"

    const labelSpan = document.createElement("div")
    labelSpan.className = "text-xs font-semibold font-['Neue Haas Grotesk Text Pro'] leading-[18px]"
    labelSpan.textContent = chip.label
    el.appendChild(labelSpan)

    const btn = document.createElement("div")
    btn.className = "cursor-pointer"
    btn.setAttribute("role", "button")
    btn.setAttribute("aria-label", `Remove ${chip.label}`)
    btn.innerHTML = `<svg width="12" height="12" viewBox="0 0 32 32" fill="none" class="flex-shrink-0"><path d="M25 7L7 25" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M25 25L7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    btn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.removeChip(chip.key, chip.value)
    })
    el.appendChild(btn)

    return el
  }

  // --- Category list ---

  renderCategoryList() {
    const container = this.categoryListTarget
    container.innerHTML = ""

    const addEyebrow = (label) => {
      const eyebrow = document.createElement("div")
      eyebrow.className = "text-[10px] font-semibold text-gray-400 uppercase tracking-[0.08em] px-3 pt-3 pb-1.5"
      eyebrow.textContent = label
      container.appendChild(eyebrow)
    }

    let section = null

    for (const config of this.configValue) {
      const nextSection = config.favorite ? "FAVORITES" : "FILTER BY"
      if (nextSection !== section) {
        addEyebrow(nextSection)
        section = nextSection
      }

      const btn = document.createElement("button")
      const isActive = this.selectedCategory === config.key
      const count = (this.draftFilters[config.key] || []).length

      btn.className = `flex items-center gap-2 text-left cursor-pointer rounded-md transition-all duration-100 py-[7px] px-[10px] text-[13px] border ${
        isActive
          ? "bg-core-purple-200 border-core-purple-300 font-semibold text-core-purple-500"
          : "bg-transparent border-transparent font-medium text-gray-700 hover:bg-support-light-250 hover:border-support-light-350"
      }`
      btn.style.width = "calc(100% - 16px)"
      btn.style.margin = "0 8px"

      const hasSelections = count > 0
      btn.innerHTML = `
        ${hasSelections ? this.checkCircleIcon() : this.categoryIcon(config.key, config.type)}
        <span class="overflow-hidden text-ellipsis whitespace-nowrap flex-1">${config.label}</span>
      `

      btn.addEventListener("click", () => {
        this.selectedCategory = config.key
        this.renderCategoryList()
        this.renderRightPanel()
      })

      container.appendChild(btn)
    }
  }

  checkCircleIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" class="flex-shrink-0 fill-core-purple-500"><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"/></svg>`
  }

  categoryIcon(key, type) {
    const icons = {
      claim_id: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M72,104a8,8,0,0,1,8-8h96a8,8,0,0,1,0,16H80A8,8,0,0,1,72,104Zm8,40h96a8,8,0,0,0,0-16H80a8,8,0,0,0,0,16ZM232,56V208a8,8,0,0,1-11.58,7.15L192,200.94l-28.42,14.21a8,8,0,0,1-7.16,0L128,200.94,99.58,215.15a8,8,0,0,1-7.16,0L64,200.94,35.58,215.15A8,8,0,0,1,24,208V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56Zm-16,0H40V195.06l20.42-10.22a8,8,0,0,1,7.16,0L96,199.06l28.42-14.22a8,8,0,0,1,7.16,0L160,199.06l28.42-14.22a8,8,0,0,1,7.16,0L216,195.06Z"/></svg>`,
      employer_id: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M72,104a8,8,0,0,1,8-8h96a8,8,0,0,1,0,16H80A8,8,0,0,1,72,104Zm8,40h96a8,8,0,0,0,0-16H80a8,8,0,0,0,0,16ZM232,56V208a8,8,0,0,1-11.58,7.15L192,200.94l-28.42,14.21a8,8,0,0,1-7.16,0L128,200.94,99.58,215.15a8,8,0,0,1-7.16,0L64,200.94,35.58,215.15A8,8,0,0,1,24,208V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56Zm-16,0H40V195.06l20.42-10.22a8,8,0,0,1,7.16,0L96,199.06l28.42-14.22a8,8,0,0,1,7.16,0L160,199.06l28.42-14.22a8,8,0,0,1,7.16,0L216,195.06Z"/></svg>`,
      employer: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M224,64H176V56a24,24,0,0,0-24-24H104A24,24,0,0,0,80,56v8H32A16,16,0,0,0,16,80V192a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V80A16,16,0,0,0,224,64ZM96,56a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96ZM224,80v32H192v-8a8,8,0,0,0-16,0v8H80v-8a8,8,0,0,0-16,0v8H32V80Zm0,112H32V128H64v8a8,8,0,0,0,16,0v-8h96v8a8,8,0,0,0,16,0v-8h32v64Z"/></svg>`,
      member_name: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z"/></svg>`,
      member_id: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M200,112a8,8,0,0,1-8,8H152a8,8,0,0,1,0-16h40A8,8,0,0,1,200,112Zm-8,24H152a8,8,0,0,0,0,16h40a8,8,0,0,0,0-16Zm40-80V200a16,16,0,0,1-16,16H40a16,16,0,0,1-16-16V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56ZM216,200V56H40V200ZM112,152a32,32,0,1,0-32-32A32,32,0,0,0,112,152Zm0-48a16,16,0,1,1-16,16A16,16,0,0,1,112,104Zm43.21,64a8,8,0,0,1-6.94,4H63.72a8,8,0,0,1-6.94-4,8,8,0,0,1,0-8,48,48,0,0,1,82.44,0A8,8,0,0,1,155.21,168Z"/></svg>`,
      date_of_birth: `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45"><rect x="1.5" y="2.5" width="13" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M1.5 6.5h13" stroke="currentColor" stroke-width="1.5"/><path d="M5 1v3M11 1v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`,
      location: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M240,208H224V96a16,16,0,0,0-16-16H144V32a16,16,0,0,0-24.88-13.32L39.12,72A16,16,0,0,0,32,85.34V208H16a8,8,0,0,0,0,16H240a8,8,0,0,0,0-16ZM208,96V208H144V96ZM48,85.34,128,32V208H48ZM112,112v16a8,8,0,0,1-16,0V112a8,8,0,1,1,16,0Zm-32,0v16a8,8,0,0,1-16,0V112a8,8,0,1,1,16,0Zm0,56v16a8,8,0,0,1-16,0V168a8,8,0,0,1,16,0Zm32,0v16a8,8,0,0,1-16,0V168a8,8,0,0,1,16,0Z"/></svg>`
    }
    if (icons[key]) return icons[key]
    // Fallback icons by type
    if (type === "text_input") {
      return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
        <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" stroke-width="1.5"/>
        <path d="M10 10l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`
    }
    if (type === "currency_range") {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor" class="flex-shrink-0 opacity-45"><path d="M152,120H136V56h8a32,32,0,0,1,32,32,8,8,0,0,0,16,0,48.05,48.05,0,0,0-48-48h-8V24a8,8,0,0,0-16,0V40H112a48,48,0,0,0,0,96h8v64H104a32,32,0,0,1-32-32,8,8,0,0,0-16,0,48.05,48.05,0,0,0,48,48h16v16a8,8,0,0,0,16,0V216h16a48,48,0,0,0,0-96Zm-32,0H112a32,32,0,0,1,0-64h8Zm32,80H136V136h16a32,32,0,0,1,0,64Z"/></svg>`
    }
    if (type === "integer_range") {
      return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
        <path d="M5.5 1.5l-2 13M12.5 1.5l-2 13M1.5 5.5h13M1.5 10.5h13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`
    }
    if (type === "date_range") {
      return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
        <rect x="1.5" y="2.5" width="13" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
        <path d="M1.5 6.5h13" stroke="currentColor" stroke-width="1.5"/>
        <path d="M5 1v3M11 1v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`
    }
    if (type === "enum_select") {
      return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
        <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/>
        <path d="M9 3.5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <rect x="1" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/>
        <path d="M9 11.5h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`
    }
    if (type === "boolean_select") {
      return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
        <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/>
        <circle cx="8" cy="8" r="3" fill="currentColor"/>
      </svg>`
    }
    return `<svg width="13" height="13" viewBox="0 0 16 16" fill="none" class="flex-shrink-0 opacity-45">
      <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" stroke-width="1.5"/>
      <path d="M10 10l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`
  }

  // --- Right panel rendering ---

  renderRightPanel() {
    const config = this.configValue.find(f => f.key === this.selectedCategory)
    if (!config) return

    const panel = this.rightPanelTarget
    panel.innerHTML = ""

    if (config.type === "text_search") {
      this.renderTextSearchPanel(panel, config)
    } else if (config.type === "enum_select") {
      this.renderEnumSelectPanel(panel, config)
    } else if (config.type === "date_input") {
      this.renderDateInputPanel(panel, config)
    } else if (config.type === "date_range") {
      this.renderDateRangePanel(panel, config)
    } else if (config.type === "currency_range") {
      this.renderCurrencyRangePanel(panel, config)
    } else if (config.type === "integer_range") {
      this.renderIntegerRangePanel(panel, config)
    } else if (config.type === "boolean_select") {
      this.renderBooleanSelectPanel(panel, config)
    } else if (config.type === "text_input") {
      this.renderTextInputPanel(panel, config)
    }
  }

  // --- Text Search Panel ---

  renderTextSearchPanel(container, config) {
    const selected = this.draftFilters[config.key] || []

    // Search input
    const inputWrap = document.createElement("div")
    inputWrap.className = "px-4 pt-3 pb-2"
    const input = document.createElement("input")
    input.type = "text"
    input.placeholder = config.placeholder || "Search..."
    input.className = "w-full px-3 py-2 border-[1.5px] border-core-purple-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500"
    inputWrap.appendChild(input)
    container.appendChild(inputWrap)

    // Selected items area (checked items above hr)
    const selectedArea = document.createElement("div")
    selectedArea.className = "px-4"
    container.appendChild(selectedArea)

    // Divider (only shown when there are selected items)
    const divider = document.createElement("hr")
    divider.className = "mx-4 border-gray-200"
    container.appendChild(divider)

    // Results area
    const resultsArea = document.createElement("div")
    resultsArea.className = "flex-1 overflow-y-auto px-4 pb-3"
    container.appendChild(resultsArea)

    const renderSelectedItems = () => {
      const currentSelected = this.draftFilters[config.key] || []
      selectedArea.innerHTML = ""
      divider.classList.toggle("hidden", currentSelected.length === 0)
      for (const val of currentSelected) {
        const row = this.createSearchResultButton(val, true, () => {
          this.toggleDraftValue(config.key, val)
          renderSelectedItems()
          doSearch(input.value)
          this.renderCategoryList()
          this.updateDraftCount()
        })
        selectedArea.appendChild(row)
      }
    }
    renderSelectedItems()

    // Search handler
    const doSearch = (query) => {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      if (this.abortController) this.abortController.abort()

      const delay = query ? 150 : 0
      this.searchTimeout = setTimeout(async () => {
        resultsArea.innerHTML = `<div class="text-gray-400 text-[13px] py-2">Searching...</div>`

        this.abortController = new AbortController()
        try {
          const url = new URL(this.autocompleteUrlValue, window.location.origin)
          url.searchParams.set("field", config.key)
          url.searchParams.set("q", query)

          const response = await fetch(url.toString(), {
            signal: this.abortController.signal,
            headers: { "Accept": "application/json" }
          })
          const results = await response.json()

          resultsArea.innerHTML = ""
          const currentSelected = this.draftFilters[config.key] || []

          // Show "Starts with" and "Contains" options when there's a query
          if (query.trim()) {
            const trimmed = query.trim()
            const startsWithVal = `starts_with:${trimmed}`
            const containsVal = `contains:${trimmed}`

            if (!currentSelected.includes(startsWithVal)) {
              const swBtn = this.createSearchResultButton(startsWithVal, false, () => {
                this.toggleDraftValue(config.key, startsWithVal)
                renderSelectedItems()
                doSearch(input.value)
                this.renderCategoryList()
                this.updateDraftCount()
              })
              resultsArea.appendChild(swBtn)
            }
            if (!currentSelected.includes(containsVal)) {
              const cBtn = this.createSearchResultButton(containsVal, false, () => {
                this.toggleDraftValue(config.key, containsVal)
                renderSelectedItems()
                doSearch(input.value)
                this.renderCategoryList()
                this.updateDraftCount()
              })
              resultsArea.appendChild(cBtn)
            }
          }

          if (results.length === 0 && query) {
            if (!query.trim()) {
              resultsArea.innerHTML = `<div class="text-gray-400 text-[13px] py-2">No results found</div>`
            }
            return
          }

          // Filter out already-selected items from the results list
          const unselectedResults = results.filter(r => !currentSelected.includes(r))

          for (const result of unselectedResults) {
            const btn = this.createSearchResultButton(result, false, () => {
              this.toggleDraftValue(config.key, result)
              renderSelectedItems()
              doSearch(input.value)
              this.renderCategoryList()
              this.updateDraftCount()
            })
            resultsArea.appendChild(btn)
          }
        } catch (e) {
          if (e.name !== "AbortError") {
            resultsArea.innerHTML = `<div class="text-gray-400 text-[13px] py-2">Error searching</div>`
          }
        }
      }, delay)
    }

    input.addEventListener("input", () => doSearch(input.value))

    // Focus input after render
    requestAnimationFrame(() => input.focus())

    // Initial search (empty query)
    doSearch("")
  }

  // renderSelectedMiniChips is no longer used — selected items are now shown
  // as checked rows above an <hr> divider in the search panel (see renderTextSearchPanel)

  createSearchResultButton(result, isSelected, onClick) {
    const btn = document.createElement("button")
    btn.className = `flex items-center gap-2 w-full text-left border-none py-[7px] px-2 text-[13px] cursor-pointer rounded transition-all duration-100 ${
      isSelected ? "bg-core-purple-200 text-core-purple-500 font-semibold" : "bg-transparent text-gray-700 font-normal hover:bg-gray-50"
    }`

    const displayLabel = this.displayLabelForValue(result)
    btn.innerHTML = `
      <div class="w-4 h-4 rounded-[3px] border flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
        isSelected ? "border-core-purple-500 bg-core-purple-500" : "border-gray-300 bg-white"
      }">
        ${isSelected ? `<svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>` : ""}
      </div>
      ${displayLabel}
    `
    btn.addEventListener("click", onClick)
    return btn
  }

  displayLabelForValue(value) {
    if (value.startsWith("starts_with:")) {
      const q = value.slice("starts_with:".length)
      return `Starts with ${this.escapeHtml(q)}`
    }
    if (value.startsWith("contains:")) {
      const q = value.slice("contains:".length)
      return `Contains ${this.escapeHtml(q)}`
    }
    return this.escapeHtml(value)
  }

  // --- Enum Select Panel ---

  renderEnumSelectPanel(container, config) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 overflow-y-auto p-4 flex flex-col gap-0.5"

    const rows = []

    const parentState = (parentOpt) => {
      const currentSelected = this.draftFilters[config.key] || []
      const childValues = (parentOpt.children || []).map(c => c.value)
      if (childValues.length === 0) return "unchecked"
      const checkedCount = childValues.filter(v => currentSelected.includes(v)).length
      if (checkedCount === childValues.length) return "checked"
      if (checkedCount > 0) return "indeterminate"
      return "unchecked"
    }

    const optState = (opt) => {
      const currentSelected = this.draftFilters[config.key] || []
      if (Array.isArray(opt.children) && opt.children.length > 0) {
        return parentState(opt)
      }
      return currentSelected.includes(opt.value) ? "checked" : "unchecked"
    }

    const checkboxIcon = (state) => {
      if (state === "checked") {
        return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
      }
      if (state === "indeterminate") {
        return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 6h6" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>`
      }
      return ""
    }

    const applyRowStyles = (row, opt, isChild) => {
      const state = optState(opt)
      const isHighlighted = state !== "unchecked"
      row.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
        isHighlighted ? "bg-core-purple-200" : "bg-transparent hover:bg-gray-50"
      } ${isChild ? "ml-6" : ""}`
      const checkbox = row.querySelector("[data-checkbox]")
      if (checkbox) {
        checkbox.className = `w-[18px] h-[18px] rounded flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
          isHighlighted ? "border-core-purple-500 bg-core-purple-500" : "border-gray-300 bg-white"
        }`
        checkbox.innerHTML = checkboxIcon(state)
      }
    }

    const updateItems = () => {
      for (const item of rows) {
        applyRowStyles(item.row, item.opt, item.isChild)
      }
    }

    const buildRow = (opt, isChild) => {
      const row = document.createElement("label")
      row.dataset.optValue = opt.value
      row.innerHTML = `
        <div data-checkbox></div>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-semibold text-gray-800 leading-tight">${this.escapeHtml(opt.label)}</div>
          ${opt.description ? `<div class="text-xs text-support-dark-400 mt-0.5 leading-snug">${this.escapeHtml(opt.description)}</div>` : ""}
        </div>
      `
      applyRowStyles(row, opt, isChild)
      return row
    }

    const setSelection = (next) => {
      if (next.length === 0) {
        delete this.draftFilters[config.key]
      } else {
        this.draftFilters[config.key] = next
      }
    }

    for (const opt of (config.options || [])) {
      const hasChildren = Array.isArray(opt.children) && opt.children.length > 0
      const parentRow = buildRow(opt, false)

      parentRow.addEventListener("click", (e) => {
        e.preventDefault()
        if (hasChildren) {
          const childValues = opt.children.map(c => c.value)
          const current = this.draftFilters[config.key] || []
          const allChecked = childValues.every(v => current.includes(v))
          let next = current.filter(v => !childValues.includes(v))
          if (!allChecked) next = next.concat(childValues)
          setSelection(next)
        } else {
          this.toggleDraftValue(config.key, opt.value)
        }
        updateItems()
        this.renderCategoryList()
        this.updateDraftCount()
      })

      rows.push({ row: parentRow, opt, isChild: false })
      wrapper.appendChild(parentRow)

      if (hasChildren) {
        for (const child of opt.children) {
          const childRow = buildRow(child, true)
          childRow.addEventListener("click", (e) => {
            e.preventDefault()
            this.toggleDraftValue(config.key, child.value)
            updateItems()
            this.renderCategoryList()
            this.updateDraftCount()
          })
          rows.push({ row: childRow, opt: child, isChild: true })
          wrapper.appendChild(childRow)
        }
      }
    }

    container.appendChild(wrapper)
  }

  findEnumOption(config, value) {
    for (const opt of (config.options || [])) {
      if (opt.value === value) return opt
      if (Array.isArray(opt.children)) {
        const child = opt.children.find(c => c.value === value)
        if (child) return child
      }
    }
    return null
  }

  // --- Date Input Panel (single mm/dd/yyyy) ---

  renderDateInputPanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    let dateVal = currentValues.length > 0 ? currentValues[0] : ""

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const labelDiv = document.createElement("div")
    labelDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">${this.escapeHtml(config.label)}</div>`

    const input = document.createElement("input")
    input.type = "text"
    input.placeholder = config.placeholder || "mm/dd/yyyy"
    input.value = dateVal
    input.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500"

    input.addEventListener("change", () => {
      const val = input.value.trim()
      if (val) {
        this.draftFilters[config.key] = [val]
      } else {
        delete this.draftFilters[config.key]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    })

    labelDiv.appendChild(input)
    wrapper.appendChild(labelDiv)

    if (dateVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear"
      clearBtn.addEventListener("click", () => {
        input.value = ""
        delete this.draftFilters[config.key]
        this.renderCategoryList()
        this.updateDraftCount()
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  // --- Date Range Panel ---

  renderDateRangePanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    let fromVal = ""
    let toVal = ""
    let relativePreset = null
    if (currentValues.length > 0) {
      const currentValue = currentValues[0] || ""
      if (this.isRelativeDateRange(currentValue)) {
        relativePreset = this.relativePresetKey(currentValue)
        ;[fromVal, toVal] = this.resolveRelativeDateRange(relativePreset)
      } else {
        const parsed = currentValue.split("|")
        fromVal = parsed[0] || ""
        toVal = parsed[1] || ""
      }
    }

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    // Date inputs row
    const dateRow = document.createElement("div")
    dateRow.className = "flex gap-3 items-center"

    // From
    const fromDiv = document.createElement("div")
    fromDiv.className = "flex-1"
    fromDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">From</div>`
    const fromInput = document.createElement("input")
    fromInput.type = "date"
    fromInput.value = fromVal
    fromInput.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500"
    fromDiv.appendChild(fromInput)

    // Arrow
    const arrow = document.createElement("div")
    arrow.className = "text-gray-300 text-base mt-[18px]"
    arrow.textContent = "→"

    // To
    const toDiv = document.createElement("div")
    toDiv.className = "flex-1"
    toDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">To</div>`
    const toInput = document.createElement("input")
    toInput.type = "date"
    toInput.value = toVal
    toInput.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500"
    toDiv.appendChild(toInput)

    dateRow.append(fromDiv, arrow, toDiv)
    wrapper.appendChild(dateRow)
    const updateConstraints = () => {
      if (!config.require_complete) return
      fromInput.required = toInput.required = !!(fromVal || toVal)
      fromInput.max = toVal
      toInput.min = fromVal
    }
    updateConstraints()
    if (config.require_complete) {
      const hint = document.createElement("p")
      hint.className = "text-xs text-support-dark-500"
      hint.textContent = "Choose both a start and end date."
      wrapper.appendChild(hint)
    }

    const commitDates = (f, t, relative = null) => {
      updateConstraints()
      if (!f && !t && !relative) {
        delete this.draftFilters[config.key]
      } else if (relative) {
        this.draftFilters[config.key] = [`relative:${relative}`]
      } else {
        this.draftFilters[config.key] = [`${f}|${t}`]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    }

    fromInput.addEventListener("change", () => {
      fromVal = fromInput.value
      relativePreset = null
      commitDates(fromVal, toVal)
    })
    toInput.addEventListener("change", () => {
      toVal = toInput.value
      relativePreset = null
      commitDates(fromVal, toVal)
    })

    // Quick select dropdown
    const presetsDiv = document.createElement("div")
    presetsDiv.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-2 uppercase tracking-wider">Quick select</div>`

    const defaults = [
      { label: "Today", key: "today", fn: () => this.resolveRelativeDateRange("today") },
      { label: "Last 7 days", key: "last_7_days", fn: () => this.resolveRelativeDateRange("last_7_days") },
      { label: "Last 30 days", key: "last_30_days", fn: () => this.resolveRelativeDateRange("last_30_days") },
      { label: "Last 90 days", key: "last_90_days", fn: () => this.resolveRelativeDateRange("last_90_days") },
      { label: "This month", key: "this_month", fn: () => this.resolveRelativeDateRange("this_month") },
      { label: "This year", key: "this_year", fn: () => this.resolveRelativeDateRange("this_year") }
    ]
    const presets = config.presets ? config.presets.map(p => ({ ...p, fn: () => this.resolveRelativeDateRange(p.key) })) : defaults

    // Determine which preset is currently selected
    const currentPresetLabel = relativePreset ? presets.find(p => p.key === relativePreset)?.label : this.matchPreset(fromVal, toVal, presets)

    const select = document.createElement("select")
    select.className = "w-full px-2.5 py-[7px] border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white outline-none box-border focus:border-core-purple-500 cursor-pointer"

    const defaultOpt = document.createElement("option")
    defaultOpt.value = ""
    defaultOpt.textContent = "Select a range..."
    if (!currentPresetLabel) defaultOpt.selected = true
    select.appendChild(defaultOpt)

    for (const p of presets) {
      const opt = document.createElement("option")
      opt.value = p.label
      opt.textContent = p.label
      if (currentPresetLabel === p.label) opt.selected = true
      select.appendChild(opt)
    }

    select.addEventListener("change", () => {
      const chosen = presets.find(p => p.label === select.value)
      if (chosen) {
        const [f, t] = chosen.fn()
        fromInput.value = f
        toInput.value = t
        fromVal = f
        toVal = t
        relativePreset = chosen.key
        commitDates(f, t, chosen.key)
      } else {
        // "Select a range..." chosen — don't clear dates
      }
    })

    presetsDiv.appendChild(select)
    wrapper.appendChild(presetsDiv)

    // Clone the standard Rails checkbox; comparison shares the date filter's draft/apply lifecycle.
    if (config.comparison) {
      const template = Array.from(this.element.querySelectorAll("template[data-claims-filter-comparison-key]"))
        .find(el => el.dataset.claimsFilterComparisonKey === config.comparison.key)
      if (template) {
        const comparison = template.content.cloneNode(true)
        const checkbox = comparison.querySelector('input[type="checkbox"]')
        checkbox.checked = this.comparisonEnabled(config, this.draftFilters)
        checkbox.addEventListener("change", () => {
          this.draftFilters[config.comparison.key] = [checkbox.checked ? "1" : "0"]
        })
        wrapper.appendChild(comparison)
      }
    }

    // Clear dates link
    if (fromVal || toVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear dates"
      clearBtn.addEventListener("click", () => {
        fromInput.value = ""
        toInput.value = ""
        fromVal = ""
        toVal = ""
        relativePreset = null
        commitDates("", "")
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  // Match current from/to against preset functions to detect which is active
  matchPreset(fromVal, toVal, presets) {
    if (!fromVal && !toVal) return null
    for (const p of presets) {
      const [f, t] = p.fn()
      if (f === fromVal && t === toVal) return p.label
    }
    return null
  }

  // --- Currency Range Panel ---

  renderCurrencyRangePanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    // Stored as "exact|min|max"
    const parsed = currentValues.length > 0 ? currentValues[0].split("|") : ["", "", ""]
    let exactVal = parsed[0] || ""
    let minVal = parsed[1] || ""
    let maxVal = parsed[2] || ""

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const commitAmounts = () => {
      if (!exactVal && !minVal && !maxVal) {
        delete this.draftFilters[config.key]
      } else {
        this.draftFilters[config.key] = [`${exactVal}|${minVal}|${maxVal}`]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    }

    // Helper to create a currency input row using the app's standard currency input pattern
    const createAmountInput = (labelText, value, onChange) => {
      const row = document.createElement("div")
      row.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">${labelText}</div>`
      const inputWrap = document.createElement("div")
      inputWrap.className = "relative"
      const input = document.createElement("input")
      input.type = "text"
      input.inputMode = "decimal"
      input.value = value
      input.placeholder = "0.00"
      input.className = "w-full currency"
      input.dataset.controller = "format-number"
      const prefix = document.createElement("div")
      prefix.className = "absolute top-0 left-0 z-10 text-support-dark-700 h-full py-2 px-4"
      prefix.textContent = "$"
      input.addEventListener("input", () => {
        // Strip formatting to get raw numeric value for filter storage
        const raw = input.value.replace(/[^0-9.]/g, "")
        onChange(raw)
        commitAmounts()
      })
      inputWrap.append(input, prefix)
      row.appendChild(inputWrap)
      return row
    }

    // Specific amount
    wrapper.appendChild(createAmountInput("Specific amount", exactVal, (v) => { exactVal = v }))

    // Divider
    const divider = document.createElement("div")
    divider.className = "flex items-center gap-2"
    divider.innerHTML = `<hr class="flex-1 border-gray-200"><span class="text-[11px] font-semibold text-gray-400 uppercase">or</span><hr class="flex-1 border-gray-200">`
    wrapper.appendChild(divider)

    // At least
    wrapper.appendChild(createAmountInput("At least", minVal, (v) => { minVal = v }))

    // No more than
    wrapper.appendChild(createAmountInput("No more than", maxVal, (v) => { maxVal = v }))

    // Clear link
    if (exactVal || minVal || maxVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear amounts"
      clearBtn.addEventListener("click", () => {
        exactVal = ""
        minVal = ""
        maxVal = ""
        commitAmounts()
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  // --- Integer Range Panel ---
  // Mirrors renderCurrencyRangePanel but for plain whole-number values (no currency
  // formatting, no "$" prefix) — e.g. CHS negotiation status update attempt counts.

  renderIntegerRangePanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    // Stored as "exact|min|max"
    const parsed = currentValues.length > 0 ? currentValues[0].split("|") : ["", "", ""]
    let exactVal = parsed[0] || ""
    let minVal = parsed[1] || ""
    let maxVal = parsed[2] || ""

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const commitValues = () => {
      if (!exactVal && !minVal && !maxVal) {
        delete this.draftFilters[config.key]
      } else {
        this.draftFilters[config.key] = [`${exactVal}|${minVal}|${maxVal}`]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    }

    const createIntegerInput = (labelText, value, onChange) => {
      const row = document.createElement("div")
      row.innerHTML = `<div class="text-[11px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">${labelText}</div>`
      const input = document.createElement("input")
      input.type = "text"
      input.inputMode = "numeric"
      input.value = value
      input.placeholder = "0"
      input.className = "w-full"
      input.addEventListener("input", () => {
        // Whole numbers only (attempts can't be fractional or negative). Sanitize only the
        // value handed to the filter, not input.value itself — rewriting input.value mid-typing
        // would jump the caret to the end (mirrors createAmountInput's approach above).
        const raw = input.value.replace(/[^0-9]/g, "")
        onChange(raw)
        commitValues()
      })
      row.appendChild(input)
      return row
    }

    // Specific amount
    wrapper.appendChild(createIntegerInput("Specific amount", exactVal, (v) => { exactVal = v }))

    // Divider
    const divider = document.createElement("div")
    divider.className = "flex items-center gap-2"
    divider.innerHTML = `<hr class="flex-1 border-gray-200"><span class="text-[11px] font-semibold text-gray-400 uppercase">or</span><hr class="flex-1 border-gray-200">`
    wrapper.appendChild(divider)

    // At least
    wrapper.appendChild(createIntegerInput("At least", minVal, (v) => { minVal = v }))

    // No more than
    wrapper.appendChild(createIntegerInput("No more than", maxVal, (v) => { maxVal = v }))

    // Clear link
    if (exactVal || minVal || maxVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear"
      clearBtn.addEventListener("click", () => {
        exactVal = ""
        minVal = ""
        maxVal = ""
        commitValues()
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
  }

  // --- Boolean Select Panel ---

  renderBooleanSelectPanel(container, config) {
    const selected = this.draftFilters[config.key] || []
    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 overflow-y-auto p-4 flex flex-col gap-0.5"

    const updateItems = () => {
      const currentSelected = this.draftFilters[config.key] || []
      for (const item of wrapper.children) {
        const optValue = item.dataset.optValue
        const isChecked = currentSelected.includes(optValue)
        item.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
          isChecked ? "bg-core-purple-200" : "bg-transparent hover:bg-gray-50"
        }`
        const radio = item.querySelector("[data-radio]")
        if (radio) {
          radio.className = `w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
            isChecked ? "border-core-purple-500 bg-core-purple-500" : "border-gray-300 bg-white"
          }`
          radio.innerHTML = isChecked ? `<div class="w-[8px] h-[8px] rounded-full bg-white"></div>` : ""
        }
      }
    }

    for (const opt of (config.options || [])) {
      const isChecked = selected.includes(opt.value)
      const label = document.createElement("label")
      label.dataset.optValue = opt.value
      label.className = `flex items-start gap-2.5 p-2 cursor-pointer rounded-md transition-all duration-100 ${
        isChecked ? "bg-core-purple-200" : "bg-transparent hover:bg-gray-50"
      }`

      label.innerHTML = `
        <div data-radio class="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-[1px] transition-all duration-150 border ${
          isChecked ? "border-core-purple-500 bg-core-purple-500" : "border-gray-300 bg-white"
        }">
          ${isChecked ? `<div class="w-[8px] h-[8px] rounded-full bg-white"></div>` : ""}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-semibold text-gray-800 leading-tight">${this.escapeHtml(opt.label)}</div>
          ${opt.description ? `<div class="text-xs text-support-dark-400 mt-0.5 leading-snug">${this.escapeHtml(opt.description)}</div>` : ""}
        </div>
      `

      label.addEventListener("click", (e) => {
        e.preventDefault()
        // Boolean select: only one value at a time (radio behavior)
        const currentSelected = this.draftFilters[config.key] || []
        if (currentSelected.includes(opt.value)) {
          delete this.draftFilters[config.key]
        } else {
          this.draftFilters[config.key] = [opt.value]
        }
        updateItems()
        this.renderCategoryList()
        this.updateDraftCount()
      })

      wrapper.appendChild(label)
    }

    container.appendChild(wrapper)
  }

  // --- Text Input Panel (simple single-value text field, no autocomplete) ---

  renderTextInputPanel(container, config) {
    const currentValues = this.draftFilters[config.key] || []
    let textVal = currentValues.length > 0 ? currentValues[0] : ""

    const wrapper = document.createElement("div")
    wrapper.className = "flex-1 p-4 flex flex-col gap-4"

    const labelDiv = document.createElement("div")
    labelDiv.innerHTML = `<div class="text-[11px] font-semibold text-support-dark-300 mb-1 uppercase tracking-wider">${this.escapeHtml(config.label)}</div>`

    const input = document.createElement("input")
    input.type = "text"
    input.placeholder = config.placeholder || `Filter by ${config.label.toLowerCase()}...`
    input.value = textVal
    input.className = "w-full px-2.5 py-[7px] border border-support-light-350 rounded-md text-[13px] text-support-dark-700 bg-white outline-none box-border focus:border-core-purple-500"

    input.addEventListener("input", () => {
      const val = input.value.trim()
      if (val) {
        this.draftFilters[config.key] = [val]
      } else {
        delete this.draftFilters[config.key]
      }
      this.renderCategoryList()
      this.updateDraftCount()
    })

    labelDiv.appendChild(input)
    wrapper.appendChild(labelDiv)

    if (textVal) {
      const clearBtn = document.createElement("span")
      clearBtn.setAttribute("role", "button")
      clearBtn.setAttribute("tabindex", "0")
      clearBtn.className = "text-link text-sm"
      clearBtn.textContent = "Clear"
      clearBtn.addEventListener("click", () => {
        input.value = ""
        delete this.draftFilters[config.key]
        this.renderCategoryList()
        this.updateDraftCount()
        this.renderRightPanel()
      })
      wrapper.appendChild(clearBtn)
    }

    container.appendChild(wrapper)
    requestAnimationFrame(() => input.focus())
  }

  // --- Draft helpers ---

  comparisonEnabled(config, filters) {
    const value = filters[config.comparison.key]?.[0]
    return value === undefined ? config.comparison.default === true : value === "1"
  }

  toggleDraftValue(key, value) {
    const current = this.draftFilters[key] || []
    if (current.includes(value)) {
      const next = current.filter(v => v !== value)
      if (next.length === 0) {
        delete this.draftFilters[key]
      } else {
        this.draftFilters[key] = next
      }
    } else {
      this.draftFilters[key] = [...current, value]
    }
  }

  updateDraftCount() {
    const total = this.configValue.reduce((sum, config) => sum + (this.draftFilters[config.key] || []).length, 0)
    this.draftCountTarget.textContent = total > 0
      ? `${total} filter${total !== 1 ? "s" : ""} selected`
      : "No filters selected"
  }

  // --- Date helpers ---

  isoToday() {
    const d = new Date()
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
  }

  isRelativeDateRange(value) {
    return (value || "").startsWith("relative:")
  }

  relativePresetKey(value) {
    return (value || "").replace(/^relative:/, "")
  }

  relativePresetLabel(value) {
    const labels = {
      today: "Today",
      last_7_days: "Last 7 days",
      last_30_days: "Last 30 days",
      last_90_days: "Last 90 days",
      this_month: "This month",
      last_month: "Last month",
      this_quarter: "This quarter",
      last_quarter: "Last quarter",
      last_year: "Last year",
      this_year: "This year"
    }
    return labels[this.relativePresetKey(value)] || value
  }

  resolveRelativeDateRange(preset) {
    const today = this.isoToday()
    if (preset === "today") return [today, today]
    if (preset === "last_7_days") return [this.isoOffset(-7), today]
    if (preset === "last_30_days") return [this.isoOffset(-30), today]
    if (preset === "last_90_days") return [this.isoOffset(-90), today]
    if (preset === "this_month") {
      const d = new Date()
      return [`${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-01`, today]
    }
    if (preset === "this_year") return [`${new Date().getFullYear()}-01-01`, today]
    const now = new Date()
    const iso = d => `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
    const quarter = Math.floor(now.getMonth() / 3) * 3
    if (preset === "last_month") return [iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), iso(new Date(now.getFullYear(), now.getMonth(), 0))]
    if (preset === "this_quarter") return [iso(new Date(now.getFullYear(), quarter, 1)), today]
    if (preset === "last_quarter") return [iso(new Date(now.getFullYear(), quarter - 3, 1)), iso(new Date(now.getFullYear(), quarter, 0))]
    if (preset === "last_year") return [`${now.getFullYear() - 1}-01-01`, `${now.getFullYear() - 1}-12-31`]
    return ["", ""]
  }

  isoOffset(days) {
    const d = new Date()
    d.setDate(d.getDate() + days)
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
  }

  pad(n) {
    return String(n).padStart(2, "0")
  }

  formatDateRange(val) {
    if (!val) return ""
    if (this.isRelativeDateRange(val)) return this.relativePresetLabel(val)
    const [from, to] = val.split("|")
    const fmt = (d) => {
      if (!d) return ""
      const dt = new Date(d + "T00:00:00")
      return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    }
    if (from && to) return `${fmt(from)} – ${fmt(to)}`
    if (from) return `After ${fmt(from)}`
    if (to) return `Before ${fmt(to)}`
    return ""
  }

  formatCurrencyRange(val) {
    if (!val) return ""
    const [exact, min, max] = val.split("|")
    const fmt = (v) => `$${parseFloat(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    if (exact) return fmt(exact)
    if (min && max) return `${fmt(min)} – ${fmt(max)}`
    if (min) return `≥ ${fmt(min)}`
    if (max) return `≤ ${fmt(max)}`
    return ""
  }

  formatIntegerRange(val) {
    if (!val) return ""
    const [exact, min, max] = val.split("|")
    const fmt = (v) => {
      const n = parseInt(v, 10)
      return Number.isFinite(n) ? n.toLocaleString("en-US") : v
    }
    if (exact) return fmt(exact)
    if (min && max) return `${fmt(min)} – ${fmt(max)}`
    if (min) return `≥ ${fmt(min)}`
    if (max) return `≤ ${fmt(max)}`
    return ""
  }

  chipLabelForValue(val) {
    if (val.startsWith("starts_with:")) {
      return `Starts with "${val.slice("starts_with:".length)}"`
    }
    if (val.startsWith("contains:")) {
      return `Contains "${val.slice("contains:".length)}"`
    }
    return val
  }

  // --- Utility ---

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
